import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { getCachedUserAttributes } from "../utils/userAttributesCache";

export type RatingDisplayStyle =
  | "bar"
  | "stars"
  | "dots"
  | "squares"
  | "number"
  | "slider";

interface RatingDisplayContextType {
  ratingDisplayStyle: RatingDisplayStyle;
  loading: boolean;
  refreshPreference: () => Promise<void>;
}

const RatingDisplayContext = createContext<RatingDisplayContextType>({
  ratingDisplayStyle: "slider",
  loading: true,
  refreshPreference: async () => {},
});

export function RatingDisplayProvider({ children }: { children: ReactNode }) {
  const [ratingDisplayStyle, setRatingDisplayStyle] =
    useState<RatingDisplayStyle>("slider");
  const [loading, setLoading] = useState(true);

  const loadPreference = async (): Promise<void> => {
    try {
      const attributes = await getCachedUserAttributes();
      const savedStyle = attributes["custom:rating_display"] as
        | RatingDisplayStyle
        | undefined;

      if (
        savedStyle &&
        ["bar", "stars", "dots", "squares", "number", "slider"].includes(
          savedStyle,
        )
      ) {
        setRatingDisplayStyle(savedStyle);
      }
    } catch (error) {
      console.error("Error loading rating display preference:", error);
      // Default to 'slider' on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPreference();

    // Listen for preference updates
    const handlePreferenceUpdate = () => {
      loadPreference();
    };

    window.addEventListener(
      "ratingDisplayPreferenceUpdated",
      handlePreferenceUpdate,
    );

    return () => {
      window.removeEventListener(
        "ratingDisplayPreferenceUpdated",
        handlePreferenceUpdate,
      );
    };
  }, []);

  return (
    <RatingDisplayContext.Provider
      value={{ ratingDisplayStyle, loading, refreshPreference: loadPreference }}
    >
      {children}
    </RatingDisplayContext.Provider>
  );
}

export function useRatingDisplay() {
  return useContext(RatingDisplayContext);
}
